/**
 * Auth RTK Query — endpoints /auth/* du Worker (docs/03-API.md).
 * Le cookie de session HttpOnly est posé par le Worker ; le token Bearer
 * renvoyé dans le corps est aussi stocké (session.slice) pour les appels
 * hors navigateur. fetch direct : le corps n'est lu qu'une fois.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { env } from '../../config/env';
import {
  credentialsReceived,
  loggedOut,
  sessionResolved,
  loadStoredToken,
} from '../../features/auth/model/session.slice';
import type { SessionUser } from '../auth/permissions';

interface AuthPayload {
  token?: string;
  user: {
    userId: string;
    email: string;
    fullName: string;
    role: 'client' | 'professional_owner' | 'professional_agent' | 'moderator' | 'admin';
    tenantId: string | null;
  };
}

function toSessionUser(u: AuthPayload['user']): SessionUser {
  return {
    id: u.userId,
    name: u.fullName,
    email: u.email,
    role: u.role,
    tenantId: u.tenantId ?? undefined,
  };
}

interface AuthResult {
  user: SessionUser | null;
  token: string | null;
  /** 403 EMAIL_NOT_VERIFIED : l'écran de code à 6 chiffres prend le relais. */
  emailNotVerified?: boolean;
}

async function authRequest(
  path: string,
  init?: RequestInit & { auth?: boolean },
): Promise<AuthResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = loadStoredToken();
  if (init?.auth !== false && token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${env.apiUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
  });
  const body = (await res.json().catch(() => null)) as
    | { data?: AuthPayload; error?: { message?: string; code?: string } }
    | null;
  if (body?.error?.code === 'EMAIL_NOT_VERIFIED') {
    return { user: null, token: null, emailNotVerified: true };
  }
  if (!res.ok || !body?.data) {
    throw new Error(body?.error?.message ?? `Erreur d'authentification (${res.status})`);
  }
  return { user: toSessionUser(body.data.user), token: body.data.token ?? null };
}

function commit(
  dispatch: (action: ReturnType<typeof credentialsReceived>) => void,
  result: AuthResult,
): SessionUser {
  // Garde : aucune réponse auth ne doit installer une session sans utilisateur.
  const user = result.user;
  if (!user) throw new Error('Session utilisateur absente de la réponse.');
  dispatch(credentialsReceived({ token: result.token, user }));
  return user;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: async () => ({ data: null as unknown }), // endpoints via queryFn
  tagTypes: ['Session'],
  endpoints: (builder) => ({
    login: builder.mutation<SessionUser | 'EMAIL_NOT_VERIFIED', { email: string; password: string }>({
      queryFn: async (credentials, { dispatch }) => {
        try {
          const result = await authRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
          });
          if (result.emailNotVerified) {
            return { data: 'EMAIL_NOT_VERIFIED' as const };
          }
          if (result.user) commit(dispatch, result);
          return { data: result.user! };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Session'],
    }),

    /** Renvoie un code (post-login refusé : e-mail en clair, sans session). */
    resendCode: builder.mutation<{ sent: boolean }, { email: string }>({
      queryFn: async ({ email }) => {
        try {
          const res = await fetch(`${env.apiUrl}/auth/verify-request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, channel: 'email' }),
          });
          const body = (await res.json().catch(() => null)) as { data?: unknown; error?: { message?: string } } | null;
          if (!res.ok) throw new Error(body?.error?.message ?? 'Envoi impossible.');
          return { data: { sent: true } };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
    }),

    /** Valide le code à 6 chiffres (compte connecté ou e-mail fourni). */
    confirmCode: builder.mutation<{ verified: boolean }, { email?: string; code: string }>({
      queryFn: async ({ email, code }, { dispatch }) => {
        try {
          const stored = loadStoredToken();
          const res = await fetch(`${env.apiUrl}/auth/verify-confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(stored ? { Authorization: `Bearer ${stored}` } : {}),
            },
            body: JSON.stringify({ email, code }),
          });
          const body = (await res.json().catch(() => null)) as { data?: { verified?: boolean }; error?: { message?: string } } | null;
          if (!res.ok || !body?.data?.verified) {
            throw new Error(body?.error?.message ?? 'Code invalide.');
          }
          // Après vérification, on relit la session pour installer/tokenize le compte.
          const session = await authRequest('/auth/session');
          if (session.user) commit(dispatch, session);
          return { data: { verified: true } };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Session'],
    }),

    register: builder.mutation<
      SessionUser,
      {
        email: string;
        password: string;
        fullName: string;
        businessName?: string;
        trade?: string;
        category?: string;
        city?: string;
        postalCode?: string;
      }
    >({
      queryFn: async (body, { dispatch }) => {
        try {
          const result = await authRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify(body),
          });
          const user = commit(dispatch, result);
          return { data: user };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Session'],
    }),

    session: builder.query<SessionUser | null, void>({
      queryFn: async (_arg, { dispatch }) => {
        try {
          const result = await authRequest('/auth/session');
          dispatch(sessionResolved(result.user));
          return { data: result.user };
        } catch {
          // 401 ou réseau indisponible : visiteur non authentifié.
          dispatch(sessionResolved(null));
          return { data: null };
        }
      },
      providesTags: ['Session'],
    }),

    logout: builder.mutation<void, void>({
      queryFn: async (_arg, { dispatch }) => {
        try {
          await authRequest('/auth/logout', { method: 'POST' });
        } catch {
          // On déconnecte localement même si l'appel échoue.
        }
        dispatch(loggedOut());
        dispatch(authApi.util.resetApiState());
        return { data: undefined };
      },
    }),

    updateProfile: builder.mutation<
      SessionUser,
      { fullName?: string; phone?: string; currentPassword?: string; newPassword?: string }
    >({
      queryFn: async (body, { dispatch }) => {
        try {
          const result = await authRequest('/auth/profile', {
            method: 'PATCH',
            body: JSON.stringify(body),
          });
          const user = commit(dispatch, result);
          return { data: user };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useSessionQuery,
  useLogoutMutation,
  useUpdateProfileMutation,
  useResendCodeMutation,
  useConfirmCodeMutation,
} = authApi;
